import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { connectLiveSocket, type LiveScope, type LiveServerFrame, type LiveSocketHandle } from '../lib/live-socket'
import { invalidateEverySubscribedScope, ScopeInvalidationDebouncer } from '../lib/live-invalidation'
import { scopeKey, SubscriptionRegistry } from './subscription-registry'

interface LiveEventsContextValue {
    /** True once the socket is open AND the "hello" handshake completed. */
    isLive: boolean
    /** True if the socket is permanently unavailable this session (4403, or the feature is disabled). */
    isPermanentlyUnavailable: boolean
    subscribe: (scope: LiveScope) => void
    unsubscribe: (scope: LiveScope) => void
    isScopeAcked: (scope: LiveScope) => boolean
    /** Registers a listener called with the scope key whenever its acked state flips. Returns an unsubscribe function. */
    onAckChange: (listener: () => void) => () => void
}

const LiveEventsContext = createContext<LiveEventsContextValue | undefined>(undefined)

/**
 * LiveEventsProvider owns exactly one WebSocket connection for `org`,
 * mounted once in routes/__root.tsx's LiveEventsGate (which reads :org
 * loosely from the URL) so it survives navigation between an org's pages
 * AND is reachable by the global header's LiveStatusIndicator — which
 * lives ABOVE any per-route layout's <Outlet/>, so a provider scoped
 * inside routes/$org.tsx could never reach it via React context. Every
 * page-level useLiveSubscription(scope) call shares this one socket via a
 * refcounted registry — a scope is only ever subscribed once on the wire
 * even if 10 components care about it, and only unsubscribed once the last
 * one unmounts.
 */
export function LiveEventsProvider({ org, children }: { org: string; children: ReactNode }) {
    const { accessToken } = useAuth()
    const queryClient = useQueryClient()

    const [isLive, setIsLive] = useState(false)
    const [isPermanentlyUnavailable, setIsPermanentlyUnavailable] = useState(false)

    // registryRef owns the refcounted subscription state (linger, refcount,
    // acked flags) via the framework-agnostic SubscriptionRegistry — split
    // out to subscription-registry.ts so its semantics are unit-testable
    // with plain vitest (see subscription-registry.test.ts), the same way
    // live-socket.ts's transport logic is testable independent of React.
    // Deliberately NOT React state: subscribing/unsubscribing happens from
    // mount/unmount effects across many components, and routing every one
    // of those through setState would cause extra renders of every consumer
    // on every single (un)subscribe anywhere in the tree. Consumers that
    // need to react to a specific scope's acked state use isScopeAcked +
    // onAckChange (a tiny pub/sub) instead of subscribing to the whole
    // registry.
    const registryRef = useRef(new SubscriptionRegistry())
    const ackListenersRef = useRef(new Set<() => void>())
    const socketRef = useRef<LiveSocketHandle | null>(null)
    // getToken must always return the CURRENT token, including on a
    // reconnect that happens long after this effect ran — a ref (not the
    // accessToken closure variable) is what makes that possible. Updated in
    // its own effect (never during render) per the react-hooks/refs rule.
    const tokenRef = useRef(accessToken)
    useEffect(() => {
        tokenRef.current = accessToken
    }, [accessToken])

    useEffect(() => {
        // Captured once per effect run (not re-read inside the cleanup
        // closure below) — registryRef.current is a stable identity for
        // this provider instance's whole lifetime, but react-hooks/
        // exhaustive-deps flags any `ref.current` read that occurs directly
        // inside a returned cleanup function, so this satisfies that rule
        // the idiomatic way instead of suppressing it.
        const registry = registryRef.current

        // Owned per connection (per effect run), so it captures THIS run's
        // queryClient + org and is torn down with them below. Coalesces the
        // steady ~250 ms stream of collection-scope `update` frames into a
        // single trailing-edge refetch per scope instead of one per frame —
        // see live-invalidation.ts's ScopeInvalidationDebouncer.
        const invalidationDebouncer = new ScopeInvalidationDebouncer(queryClient, org)

        function notifyAckListeners() {
            for (const listener of ackListenersRef.current) listener()
        }

        function handleFrame(frame: LiveServerFrame) {
            switch (frame.type) {
                case 'subscribed':
                    if (registry.markAcked(frame.entity, frame.uid)) notifyAckListeners()
                    break
                case 'unsubscribed': {
                    // No local state keyed on this today (a scope leaving the
                    // registry already implies "not acked" — see unsubscribe
                    // below), but handled explicitly for protocol completeness
                    // and so a future consumer has somewhere to hook in.
                    break
                }
                case 'update':
                    // Debounced (trailing edge) rather than invalidated
                    // synchronously: a continuously-dirty scope emits an
                    // `update` every ~250 ms (the server flush gap), and one
                    // refetch per frame is the ~4-refetches/second storm this
                    // coalesces away. A lone update still refetches once, after
                    // the window.
                    invalidationDebouncer.schedule({ entity: frame.entity, uid: frame.uid })
                    break
                case 'resync':
                    invalidateEverySubscribedScope(queryClient, org, registry.allScopes())
                    break
                case 'error':
                    // Per docs/apis/events.md: error frames are per-request
                    // rejections, not fatal. Logged for visibility during
                    // development; there is no user-facing surface for a
                    // rejected subscribe today (the caller just won't see
                    // live updates for that scope and silently falls back to
                    // polling, which is the documented degrade path).
                    console.warn(`[live-events] ${frame.code}: ${frame.title}`)
                    break
            }
        }

        const handle = connectLiveSocket(org, () => tokenRef.current, {
            onOpen: () => {
                setIsLive(true)
                registry.replay()
                // Deliberately no notifyAckListeners() here — matches the
                // pre-existing behavior: replay() already reset acked to
                // false for every replayed entry, and the follow-up
                // `subscribed` frames (handled above) are what actually
                // notify listeners once the server re-acks each scope.
            },
            onFrame: handleFrame,
            onClose: ({ permanent }) => {
                setIsLive(false)
                if (permanent) setIsPermanentlyUnavailable(true)
                // Every subscription's ack is invalidated on disconnect — a
                // reconnect (if any) will re-subscribe and re-ack via
                // registry.replay() above.
                registry.clearAllAcked()
                notifyAckListeners()
            },
        })
        socketRef.current = handle
        registry.setSendFrame((frame) => socketRef.current?.send(frame))

        return () => {
            handle.close()
            socketRef.current = null
            // Cancel any pending trailing-edge invalidation so it can't fire
            // against this now-torn-down connection's queryClient/org after
            // unmount or an `org` change.
            invalidationDebouncer.dispose()
            // Clear every pending-removal linger timer so none fires after
            // this effect's context (this specific socket handle, this
            // closure's queryClient) is gone — covers both provider unmount
            // and an `org` change tearing down this connection for a fresh
            // one. dropPendingRemovals deletes those entries outright
            // (not just timer-cleared): leaving them in place would make
            // the NEXT connection's replay() wrongly treat them as
            // still-active and resubscribe a scope whose last consumer
            // already unmounted.
            registry.dropPendingRemovals()
        }
        // Reconnecting the whole socket when `org` changes is correct — a
        // navigation to a different org's pages needs a fresh connection
        // scoped to that org's WS route. accessToken is intentionally NOT a
        // dependency: token changes are picked up via tokenRef on the next
        // (re)connect attempt without tearing down a healthy connection.
    }, [org, queryClient])

    const value = useMemo<LiveEventsContextValue>(() => ({
        isLive,
        isPermanentlyUnavailable,
        subscribe: (scope) => registryRef.current.subscribe(scope),
        unsubscribe: (scope) => registryRef.current.unsubscribe(scope),
        isScopeAcked: (scope) => registryRef.current.isScopeAcked(scope),
        onAckChange: (listener) => {
            ackListenersRef.current.add(listener)
            return () => ackListenersRef.current.delete(listener)
        },
        // isLive/isPermanentlyUnavailable are the only reactive fields;
        // everything else is stable function identity backed by refs, so
        // this memo only needs to recompute when those two flip.
    }), [isLive, isPermanentlyUnavailable])

    return <LiveEventsContext.Provider value={value}>{children}</LiveEventsContext.Provider>
}

// Outside a provider no socket exists: only logged-out routes (/login, MFA…)
// are there, since LiveEventsGate binds every signed-in page to an org. Report
// it as permanently unavailable so the header dot hides instead of showing
// "connecting" forever.
const NOT_LIVE = { isLive: false, isPermanentlyUnavailable: true } as const

/**
 * useLiveStatus reports whether the org's live socket is currently open and
 * has completed its handshake. Drives e.g. a header status dot.
 *
 * Unlike useScopeLive/useLiveSubscription, this hook tolerates being called
 * OUTSIDE a LiveEventsProvider (returning NOT_LIVE) rather than throwing —
 * it's meant to be usable from the global header (routes/__root.tsx),
 * which renders on every route including ones with no :org param at all
 * (e.g. /organizations, /no-organization), so it renders before/without
 * $org.tsx's OrganizationLayout ever mounting the provider.
 */
export function useLiveStatus(): { isLive: boolean; isPermanentlyUnavailable: boolean } {
    const context = useContext(LiveEventsContext)
    if (context === undefined) return NOT_LIVE
    return { isLive: context.isLive, isPermanentlyUnavailable: context.isPermanentlyUnavailable }
}

/**
 * useScopeLive reports whether `scope` is BOTH currently subscribed (via a
 * useLiveSubscription call somewhere, possibly this same component) AND has
 * been acked by the server ({"type":"subscribed",...} received). This is
 * the signal use-dynamic-refresh's `live` option should key off — see
 * docs/frontend/dynamic-refresh.md.
 */
export function useScopeLive(scope: LiveScope): boolean {
    const context = useContext(LiveEventsContext)
    const key = scopeKey(scope)
    const [acked, setAcked] = useState(() => context?.isScopeAcked(scope) ?? false)

    useEffect(() => {
        // No provider (logged-out routes, or the first render before the
        // session resolves): nothing is live.
        if (!context) return
        setAcked(context.isScopeAcked(scope))
        return context.onAckChange(() => setAcked(context.isScopeAcked(scope)))
        // key intentionally drives re-subscription to ack-change events
        // instead of scope (a fresh object every render) to avoid an
        // infinite effect loop for callers that don't memoize their scope.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context, key])

    return context ? acked : false
}

/**
 * useLiveSubscription subscribes to `scope` for the lifetime of the
 * component. Refcounted against every other useLiveSubscription(scope) call
 * for the same scope anywhere in the tree — the underlying WS subscribe
 * frame is sent on the first 0->1 transition and unsubscribe on the last
 * 1->0 transition, never once per component.
 *
 * Pass `enabled: false` to skip subscribing (e.g. a tab that isn't the
 * active one) without needing a conditional hook call.
 */
export function useLiveSubscription(scope: LiveScope, options?: { enabled?: boolean }): void {
    const context = useContext(LiveEventsContext)
    const enabled = options?.enabled ?? true
    const key = scopeKey(scope)

    useEffect(() => {
        if (!enabled || !context) return
        context.subscribe(scope)
        return () => context.unsubscribe(scope)
        // scope is intentionally represented by `key` in the deps array — a
        // caller passing a fresh `{ entity, uid }` object literal on every
        // render (the common case) must not re-subscribe every render; only
        // an actual entity/uid change should trigger unsubscribe+resubscribe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context, key, enabled])
}
